import React from 'react'
import { Link } from 'react-router-dom'

interface NavigationItemProps {
  icon: React.ReactNode
  label: string
  path: string
  isActive: boolean
  isExpanded: boolean
  onClick: () => void
  badge?: number | string
  shortcut?: string
  collapsed?: boolean
  indent?: boolean
}

export const NavigationItem: React.FC<NavigationItemProps> = React.memo(({
  icon,
  label,
  path,
  isActive,
  onClick,
  badge,
  shortcut,
  collapsed = false,
  indent = false,
}) => {
  return (
    <Link
      to={path}
      onClick={onClick}
      role="link"
      aria-label={`${label}${shortcut ? ` (${shortcut})` : ''}`}
      aria-current={isActive ? 'page' : undefined}
      title={collapsed ? label : undefined}
      className={`
        group relative flex items-center w-full rounded-xl transition-all duration-150
        ${collapsed ? 'h-11 justify-center px-2' : indent ? 'h-9 pl-4 pr-4 gap-3' : 'h-11 px-3 gap-3'}
        ${
          isActive
            ? 'bg-gray-900 text-white shadow-md'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }
        focus:outline-none focus:ring-2 focus:ring-admin-orange-500 focus:ring-offset-2 focus:ring-offset-white
      `}
    >
      {/* Icon */}
      <span
        className={`flex-shrink-0 ${
          indent ? 'w-5 flex items-center justify-center' : ''
        } ${isActive ? 'text-white' : 'text-gray-600 group-hover:text-gray-900'}`}
        aria-label={collapsed ? label : undefined}
      >
        {icon}
      </span>

      {/* Label — hidden when collapsed */}
      {!collapsed && (
        <span className="flex-1 text-sm font-medium truncate">{label}</span>
      )}

      {/* Badge — hidden when collapsed */}
      {!collapsed && badge !== undefined && (
        <span
          className="flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white"
          aria-label={`${badge} pending`}
        >
          {badge}
        </span>
      )}

      {/* Collapsed badge dot */}
      {collapsed && badge !== undefined && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" aria-label={`${badge} pending`} />
      )}
    </Link>
  )
})
